/**
 * „Cockpit durchsuchen“ im Nachrichtenfenster (Inhaber-Feedback 15.08.26,
 * I.6): Alle Textwerte der Falldaten (Kunde, Fahrzeuge, Gegner, Unfall,
 * Zeugen, Polizei/StA, Werte) werden zu durchsuchbaren Einträgen
 * zusammengefasst; Treffer erscheinen als Chips mit der Feldbezeichnung
 * aus backend/resources/fields_*.js und lassen sich an den Betreff anhängen.
 */

import { SUBJECT_FIELD_LABELS } from "./subjectFieldLabels";

export interface CockpitEntry {
  /** Eindeutiger Schlüssel (Bereich, Datensatz, Feld). */
  key: string;
  /** Chip-Beschriftung, z. B. „Kunde · Kennzeichen“. */
  label: string;
  /** Anzeigewert (Datum bereits als DD.MM.YYYY). */
  value: string;
  /** Vorberechnete Kleinschreibung für die Suche. */
  search: string;
}

/** Ein Datenbereich der Falldaten: Bereichs-ID (Schlüssel der Label-Map) + Datensätze. */
export interface CockpitSection {
  id: string;
  records: Array<Record<string, unknown> | null | undefined>;
}

/** Technische Felder, die nie als Betreff-Baustein taugen. */
const SKIPPED_KEYS = new Set([
  "id", "idCase", "idUser", "idVehicle", "idClient", "idOpponent", "idOrganization",
  "createdAt", "updatedAt", "deletedAt", "openedAt", "status", "uuid", "token",
]);

/** Tristate-/Boolean-Werte sind als Betreff wertlos („yes“, „no“, …). */
const SKIPPED_VALUES = new Set(["yes", "no", "unknown", "true", "false", "null", "undefined"]);

const MIN_QUERY_LENGTH = 2;
const DEFAULT_LIMIT = 30;

/** ISO-Datum (YYYY-MM-DD[T…]) → DD.MM.YYYY; andere Werte unverändert. */
export function formatCockpitValue(raw: unknown): string {
  if (raw === null || raw === undefined) return "";
  const s = String(raw).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

/** camelCase-Schlüssel ohne Bezeichnung lesbar machen („holderLastName“ → „Holder Last Name“). */
function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function isSkippedKey(key: string): boolean {
  if (SKIPPED_KEYS.has(key)) return true;
  if (/^id[A-Z]/.test(key)) return true;
  if (/(At|Hash|Token|Password)$/.test(key)) return true;
  return false;
}

function isUsableValue(raw: unknown): raw is string | number {
  if (typeof raw === "number") return Number.isFinite(raw);
  if (typeof raw !== "string") return false;
  const s = raw.trim();
  if (!s) return false;
  if (SKIPPED_VALUES.has(s.toLowerCase())) return false;
  // JSON-Blöcke, Base64 o. Ä. sind keine Betreff-Bausteine.
  if (s.length > 200 || s.startsWith("{") || s.startsWith("[")) return false;
  return true;
}

/**
 * Baut aus den Datenbereichen die flache Liste durchsuchbarer Einträge.
 * Gleiche Beschriftung + gleicher Wert erscheinen nur einmal.
 */
export function collectCockpitEntries(sections: CockpitSection[]): CockpitEntry[] {
  const out: CockpitEntry[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    const def = SUBJECT_FIELD_LABELS[section.id];
    const prefix = def?.prefix ?? humanizeKey(section.id);
    const labels = def?.labels ?? {};
    section.records.forEach((record, index) => {
      if (!record || typeof record !== "object") return;
      for (const [key, raw] of Object.entries(record)) {
        if (isSkippedKey(key) || !isUsableValue(raw)) continue;
        const value = formatCockpitValue(raw);
        if (!value) continue;
        const label = `${prefix} · ${labels[key] ?? humanizeKey(key)}`;
        const dedupe = `${label}\u0000${value}`;
        if (seen.has(dedupe)) continue;
        seen.add(dedupe);
        out.push({
          key: `${section.id}-${index}-${key}`,
          label,
          value,
          search: `${label} ${value}`.toLowerCase(),
        });
      }
    });
  }
  return out;
}

/** Ab wann eine Eingabe als Suche gilt. */
export function isCockpitQueryActive(query: string): boolean {
  return query.trim().length >= MIN_QUERY_LENGTH;
}

/**
 * Filtert die Einträge live: Jedes Wort der Eingabe muss in Beschriftung
 * oder Wert vorkommen (Groß-/Kleinschreibung egal). Treffer im Wert stehen
 * vor reinen Treffern in der Beschriftung.
 */
export function searchCockpitEntries(entries: CockpitEntry[], query: string, limit = DEFAULT_LIMIT): CockpitEntry[] {
  if (!isCockpitQueryActive(query)) return [];
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const scored: Array<{ entry: CockpitEntry; score: number }> = [];
  for (const entry of entries) {
    if (!words.every((w) => entry.search.includes(w))) continue;
    const valueLower = entry.value.toLowerCase();
    const score = words.every((w) => valueLower.includes(w)) ? 0 : 1;
    scored.push({ entry, score });
  }
  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((s) => s.entry);
}
